import React, { useEffect, useMemo, useState } from "react";
import Modal from "@mui/joy/Modal";
import ModalClose from "@mui/joy/ModalClose";
import Sheet from "@mui/joy/Sheet";
import axios from "axios";
import { toast } from "react-hot-toast";
import API_BASE_URL from "../../../config/config.js";

const TYPE_OPTIONS = [
    { value: 1, label: "Camiseta" },
    { value: 2, label: "Arquero" },
    { value: 3, label: "Libero" },
    { value: 4, label: "Medias" },
    { value: 5, label: "Shorts" },
];

const isHex6 = (v) => typeof v === "string" && /^[0-9A-Fa-f]{6}$/.test(v);
const sanitizeHex = (v) => (v || "").replace(/[^0-9A-Fa-f]/g, "").slice(0, 6).toUpperCase();

const UniformFormModal = ({ open, onClose, onSaved, participantId, initialUniform }) => {
    const isEdit = Boolean(initialUniform?.iduniform);

    const [form, setForm] = useState({
        color: "",
        type: 1,
        tone: 1,
        code: "",
        color_base: "",
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (isEdit) {
            setForm({
                color: initialUniform?.color ?? "",
                type: Number(initialUniform?.type ?? 1),
                tone: Number(initialUniform?.tone ?? 1),
                code: (initialUniform?.code ?? "").toUpperCase(),
                color_base: initialUniform?.color_base ?? "",
            });
        } else {
            setForm({ color: "", type: 1, tone: 1, code: "", color_base: "" });
        }
    }, [isEdit, initialUniform]);

    // HEX para preview y para el input type="color" (debe llevar '#')
    const hexPreview = useMemo(
        () => (form.code && isHex6(form.code) ? `#${form.code.toUpperCase()}` : "#000000"),
        [form.code]
    );

    const canSave = useMemo(() => {
        if (!participantId) return false;
        if (!form.color?.trim()) return false;
        if (!form.type) return false;
        if (!form.tone) return false;
        if (form.code && !isHex6(form.code)) return false; // opcional, pero válido si existe
        return true;
    }, [participantId, form]);

    const handleChange = (key, value) => {
        setForm((prev) => ({
            ...prev,
            [key]: key === "code" ? sanitizeHex(value) : value,
        }));
    };

    // Cuando el usuario escribe un HEX manualmente
    const handleCodeInput = (e) => {
        const next = sanitizeHex(e.target.value);
        setForm((prev) => ({ ...prev, code: next }));
        // No hace falta setear nada más: hexPreview se recalcula por useMemo
    };

    // Cuando el usuario elige en la paleta nativa <input type="color">
    const handleColorPicker = (e) => {
        // value viene como "#RRGGBB"
        const hexWithHash = e.target.value || "#000000";
        const next = hexWithHash.replace(/^#/, "").toUpperCase(); // sin '#'
        setForm((prev) => ({ ...prev, code: next }));
    };

    const handleSubmit = async (e) => {
        e?.preventDefault?.();
        if (!canSave) {
            toast.error("Completa los campos requeridos");
            return;
        }
        setSaving(true);
        try {
            if (isEdit) {
                await axios.put(`${API_BASE_URL}/uniform/${initialUniform.iduniform}`, {
                    color: form.color.trim(),
                    type: Number(form.type),
                    tone: Number(form.tone),
                    code: form.code ? form.code.toUpperCase() : "", // enviar vacío si quieres nullear
                    color_base: form.color_base || "",
                });
            } else {
                await axios.post(`${API_BASE_URL}/uniform`, {
                    idparticipant: participantId,
                    color: form.color.trim(),
                    type: Number(form.type),
                    tone: Number(form.tone),
                    code: form.code ? form.code.toUpperCase() : undefined,
                    color_base: form.color_base || undefined,
                });
            }
            onSaved?.();
        } catch (err) {
            console.error(err);
            toast.error("Ocurrió un error al guardar");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
        >
            <Sheet
                variant="outlined"
                className="
          w-[94vw] md:w-[560px] max-w-[92vw] md:max-w-[560px]
          rounded-2xl shadow-2xl border border-neutral-200 dark:border-neutral-800
          bg-white dark:bg-neutral-900 overflow-hidden
        "
            >
                <div className="px-5 py-4 border-b border-neutral-200 dark:border-neutral-800 bg-gradient-to-r from-neutral-50 to-white dark:from-neutral-900 dark:to-neutral-900">
                    <div className="flex items-center justify-between">
                        <h3 className="text-lg md:text-xl font-semibold text-neutral-900 dark:text-neutral-50">
                            {isEdit ? "Editar uniforme" : "Nuevo uniforme"}
                        </h3>
                        <ModalClose
                            variant="plain"
                            sx={{ m: 0 }}
                            className="!m-0 rounded-xl hover:scale-105 transition text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200"
                        />
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="px-5 py-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Color (requerido) */}
                        <div className="flex flex-col gap-1">
                            <label className="text-sm text-neutral-600 dark:text-neutral-300">Color *</label>
                            <input
                                type="text"
                                className="px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 outline-none focus:ring-2 focus:ring-indigo-500"
                                placeholder='Ej. "Azul Marino"'
                                value={form.color}
                                onChange={(e) => handleChange("color", e.target.value)}
                                required
                            />
                        </div>

                        {/* Type (requerido) */}
                        <div className="flex flex-col gap-1">
                            <label className="text-sm text-neutral-600 dark:text-neutral-300">Type *</label>
                            <select
                                className="px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 outline-none focus:ring-2 focus:ring-indigo-500"
                                value={form.type}
                                onChange={(e) => handleChange("type", Number(e.target.value))}
                                required
                            >
                                {TYPE_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>

                        {/* Tone (requerido) */}
                        <div className="flex flex-col gap-1">
                            <label className="text-sm text-neutral-600 dark:text-neutral-300">Tone *</label>
                            <div className="flex items-center gap-3">
                                <label className="inline-flex items-center gap-2">
                                    <input
                                        type="radio"
                                        name="tone"
                                        checked={Number(form.tone) === 1}
                                        onChange={() => handleChange("tone", 1)}
                                    />
                                    <span>Claro</span>
                                </label>
                                <label className="inline-flex items-center gap-2">
                                    <input
                                        type="radio"
                                        name="tone"
                                        checked={Number(form.tone) === 2}
                                        onChange={() => handleChange("tone", 2)}
                                    />
                                    <span>Oscuro</span>
                                </label>
                            </div>
                        </div>

                        {/* Code + Paleta (opcional y sincronizados) */}
                        <div className="flex flex-col gap-1">
                            <label className="text-sm text-neutral-600 dark:text-neutral-300">
                                Code (HEX 6 dígitos, opcional)
                            </label>
                            <div className="flex items-center gap-3">
                                {/* Paleta nativa */}
                                <input
                                    type="color"
                                    value={hexPreview}          // siempre con '#'
                                    onChange={handleColorPicker} // actualiza form.code (sin '#')
                                    className="
                    h-10 w-12 rounded-md cursor-pointer
                    border border-neutral-300 dark:border-neutral-700
                    bg-transparent p-0
                  "
                                    title="Elegir color"
                                />

                                {/* Input de HEX sin '#' */}
                                <input
                                    type="text"
                                    maxLength={6}
                                    className="flex-1 px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 outline-none focus:ring-2 focus:ring-indigo-500 font-mono"
                                    placeholder="E.g. 0B2A5B"
                                    value={form.code}
                                    onChange={handleCodeInput}
                                />

                                {/* Preview adicional (opcional) */}
                                {/* <div
                                    className="h-10 w-12 rounded-md ring-1 ring-neutral-300 dark:ring-neutral-700"
                                    style={{ backgroundColor: hexPreview }}
                                    title={hexPreview}
                                /> */}
                            </div>
                            {!!form.code && !isHex6(form.code) && (
                                <span className="text-xs text-red-600">Debe ser HEX de 6 dígitos (sin #).</span>
                            )}
                        </div>

                        {/* Color base (opcional) */}
                        <div className="flex flex-col gap-1 md:col-span-2">
                            <label className="text-sm text-neutral-600 dark:text-neutral-300">
                                Color base (opcional)
                            </label>
                            <input
                                type="text"
                                className="px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 outline-none focus:ring-2 focus:ring-indigo-500"
                                placeholder='Ej. "Azul"'
                                value={form.color_base}
                                onChange={(e) => handleChange("color_base", e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="mt-5 flex items-center justify-end gap-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="
                px-4 py-2 rounded-xl border border-neutral-300 dark:border-neutral-700
                text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800
                transition
              "
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={!canSave || saving}
                            className={`
                px-4 py-2 rounded-xl text-white transition
                ${!canSave || saving
                                    ? "bg-indigo-400 cursor-not-allowed"
                                    : "bg-indigo-600 hover:bg-indigo-700 active:scale-[.98] shadow-lg shadow-indigo-600/10"}
              `}
                        >
                            {saving ? "Guardando..." : "Guardar"}
                        </button>
                    </div>
                </form>
            </Sheet>
        </Modal>
    );
};

export default UniformFormModal;
